import { Component, ElementRef, ViewChild } from '@angular/core';
import { ReportDashboardService } from 'src/app/shared/service/report-dashboard.service';

@Component({
  selector: 'app-report-dashboard',
  templateUrl: './report-dashboard.component.html',
  styleUrls: ['./report-dashboard.component.scss']
})
export class ReportDashboardComponent {
@ViewChild('distributionCanvas') distributionCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('timelineCanvas') timelineCanvas!: ElementRef<HTMLCanvasElement>;

  distributionChart: any;
  timelineChart: any;

  // Module Configuration & Colors
  moduleConfig: Record<string, { color: string; bgClass: string; borderClass: string; textClass: string }> = {
    'Sailing Performance': { color: '#1E40AF', bgClass: 'bg-blue-500/10', borderClass: 'border-blue-500/30', textClass: 'text-blue-400' },
    'Port Performance':    { color: '#0D9488', bgClass: 'bg-teal-500/10', borderClass: 'border-teal-500/30', textClass: 'text-teal-400' },
    'Cargo Heating':       { color: '#EA580C', bgClass: 'bg-orange-500/10', borderClass: 'border-orange-500/30', textClass: 'text-orange-400' },
    'Disbursement':        { color: '#059669', bgClass: 'bg-emerald-500/10', borderClass: 'border-emerald-500/30', textClass: 'text-emerald-400' },
    'Demurrage':           { color: '#7C3AED', bgClass: 'bg-purple-500/10', borderClass: 'border-purple-500/30', textClass: 'text-purple-400' }
  };

  moduleList: string[] = ['Sailing Performance', 'Port Performance', 'Cargo Heating', 'Disbursement', 'Demurrage'];

  // Users Data
  users: User[] = [
    { id: 'usr_101', name: 'John Doe', role: 'Operations Manager', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=150', department: 'Fleet Operations' },
    { id: 'usr_102', name: 'Sarah Jenkins', role: 'Port Captain', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=150', department: 'Port Logistics' },
    { id: 'usr_103', name: 'Alex Rivera', role: 'Freight & Chartering Analyst', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=150', department: 'Commercial Desk' },
    { id: 'usr_104', name: 'Elena Rostova', role: 'Disbursements Officer', avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=150', department: 'Marine Accounting' }
  ];

  selectedUser: User = this.users[0];
  isUserDropdownOpen = false;
  userSearchQuery = '';

  // Filter States
  selectedTimeframe = '30d';
  selectedModuleFilter = 'ALL';
  tableSearchQuery = '';
  currentPage = 1;
  itemsPerPage = 6;

  // Modals & Toast State
  isReportModalOpen = false;
  isApiModalOpen = false;
  selectedReportModal: ReportItem | null = null;
  toastMessage = '';
  showToast = false;

  // Mock Database per user
  userAnalyticsData: Record<string, any> = {
    'usr_101': {
      summary: { totalReports: 142, trendPercent: 12.8, avgWeekly: 35.5, topVessel: 'M/T Nordic Mariner' },
      moduleCounts: { 'Sailing Performance': 58, 'Port Performance': 34, 'Cargo Heating': 22, 'Disbursement': 16, 'Demurrage': 12 },
      weeklyTimeline: [24, 32, 40, 46],
      reports: this.generateMockReports(142)
    },
    'usr_102': {
      summary: { totalReports: 98, trendPercent: -3.4, avgWeekly: 24.5, topVessel: 'Port of Rotterdam' },
      moduleCounts: { 'Sailing Performance': 12, 'Port Performance': 54, 'Cargo Heating': 18, 'Disbursement': 8, 'Demurrage': 6 },
      weeklyTimeline: [18, 22, 28, 30],
      reports: this.generateMockReports(98)
    },
    'usr_103': {
      summary: { totalReports: 115, trendPercent: 22.1, avgWeekly: 28.7, topVessel: 'Voyage V-2026-09' },
      moduleCounts: { 'Sailing Performance': 15, 'Port Performance': 20, 'Cargo Heating': 10, 'Disbursement': 25, 'Demurrage': 45 },
      weeklyTimeline: [15, 25, 35, 40],
      reports: this.generateMockReports(115)
    },
    'usr_104': {
      summary: { totalReports: 164, trendPercent: 8.5, avgWeekly: 41.0, topVessel: 'DA Desk Singapore' },
      moduleCounts: { 'Sailing Performance': 8, 'Port Performance': 16, 'Cargo Heating': 5, 'Disbursement': 110, 'Demurrage': 25 },
      weeklyTimeline: [30, 38, 44, 52],
      reports: this.generateMockReports(164)
    }
  };

  currentAnalytics: any;

  ngOnInit(): void {
    this.currentAnalytics = this.userAnalyticsData[this.selectedUser.id];
  }

  ngAfterViewInit(): void {
    this.renderCharts();
    this.refreshIcons();
  }

  refreshIcons(): void {
    setTimeout(() => {
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
    }, 50);
  }

  // --- User Selection ---
  toggleUserDropdown(): void {
    this.isUserDropdownOpen = !this.isUserDropdownOpen;
    this.refreshIcons();
  }

  selectUser(user: User): void {
    this.selectedUser = user;
    this.currentAnalytics = this.userAnalyticsData[user.id];
    this.isUserDropdownOpen = false;
    this.currentPage = 1;
    this.renderCharts();
    this.triggerToast(`Dashboard updated for ${user.name}`);
  }

  get filteredUsers(): User[] {
    if (!this.userSearchQuery.trim()) return this.users;
    const q = this.userSearchQuery.toLowerCase();
    return this.users.filter(u => u.name.toLowerCase().includes(q) || u.role.toLowerCase().includes(q));
  }

  // --- Filtering & Timeframe ---
  setTimeframe(tf: string): void {
    this.selectedTimeframe = tf;
    this.triggerToast(`Timeframe updated to ${tf.toUpperCase()}`);
  }

  filterByModuleCard(modName: string): void {
    this.selectedModuleFilter = this.selectedModuleFilter === modName ? 'ALL' : modName;
    this.currentPage = 1;
    this.refreshIcons();
  }

  clearModuleFilter(): void {
    this.selectedModuleFilter = 'ALL';
    this.currentPage = 1;
    this.refreshIcons();
  }

  resetFilters(): void {
    this.selectedUser = this.users[0];
    this.currentAnalytics = this.userAnalyticsData[this.selectedUser.id];
    this.selectedTimeframe = '30d';
    this.selectedModuleFilter = 'ALL';
    this.tableSearchQuery = '';
    this.currentPage = 1;
    this.renderCharts();
    this.triggerToast('Filters reset to default');
  }

  // --- Table Computations ---
  get filteredReports(): ReportItem[] {
    let list: ReportItem[] = this.currentAnalytics.reports;

    if (this.selectedModuleFilter !== 'ALL') {
      list = list.filter(r => r.module === this.selectedModuleFilter);
    }

    if (this.tableSearchQuery.trim()) {
      const q = this.tableSearchQuery.toLowerCase();
      list = list.filter(r =>
        r.id.toLowerCase().includes(q) ||
        r.title.toLowerCase().includes(q) ||
        r.vessel.toLowerCase().includes(q) ||
        r.module.toLowerCase().includes(q)
      );
    }

    return list;
  }

  get paginatedReports(): ReportItem[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    return this.filteredReports.slice(startIndex, startIndex + this.itemsPerPage);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredReports.length / this.itemsPerPage) || 1;
  }

  changePage(delta: number): void {
    this.currentPage += delta;
    this.refreshIcons();
  }

  // --- Module Calculations ---
  getModuleCount(modName: string): number {
    return this.currentAnalytics.moduleCounts[modName] || 0;
  }

  getModulePercentage(modName: string): string {
    const total = this.currentAnalytics.summary.totalReports;
    if (total === 0) return '0';
    return ((this.getModuleCount(modName) / total) * 100).toFixed(1);
  }

  getTopModuleShare(): string {
    const total = this.currentAnalytics.summary.totalReports;
    let max = 0;
    Object.values(this.currentAnalytics.moduleCounts).forEach((val: any) => {
      if (val > max) max = val;
    });
    return total > 0 ? ((max / total) * 100).toFixed(1) : '0';
  }

  getTopModuleName(): string {
    let max = 0;
    let topName = '--';
    Object.entries(this.currentAnalytics.moduleCounts).forEach(([name, count]: [string, any]) => {
      if (count > max) {
        max = count;
        topName = name;
      }
    });
    return topName;
  }

  // --- Chart Rendering ---
  renderCharts(): void {
    if (typeof Chart === 'undefined') return;

    setTimeout(() => {
      // 1. Doughnut Distribution Chart
      if (this.distributionCanvas) {
        if (this.distributionChart) this.distributionChart.destroy();

        const labels = this.moduleList;
        const dataValues = labels.map(l => this.getModuleCount(l));
        const colors = labels.map(l => this.moduleConfig[l].color);

        this.distributionChart = new Chart(this.distributionCanvas.nativeElement, {
          type: 'doughnut',
          data: {
            labels,
            datasets: [{
              data: dataValues,
              backgroundColor: colors,
              borderColor: '#0f172a',
              borderWidth: 3,
              hoverOffset: 6
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            cutout: '70%'
          }
        });
      }

      // 2. Timeline Bar Chart
      if (this.timelineCanvas) {
        if (this.timelineChart) this.timelineChart.destroy();

        this.timelineChart = new Chart(this.timelineCanvas.nativeElement, {
          type: 'bar',
          data: {
            labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4 (Current)'],
            datasets: [{
              label: 'Reports Generated',
              data: this.currentAnalytics.weeklyTimeline,
              backgroundColor: '#3B82F6',
              borderRadius: 6,
              barThickness: 24
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { display: false } },
            scales: {
              x: { grid: { display: false }, ticks: { color: '#64748b', font: { size: 10 } } },
              y: { grid: { color: '#1e293b' }, ticks: { color: '#64748b', font: { size: 10 } } }
            }
          }
        });
      }

      this.refreshIcons();
    }, 100);
  }

  // --- Modal & Toast Controls ---
  openReportModal(report: ReportItem): void {
    this.selectedReportModal = report;
    this.isReportModalOpen = true;
    this.refreshIcons();
  }

  closeReportModal(): void {
    this.isReportModalOpen = false;
  }

  toggleApiModal(show: boolean): void {
    this.isApiModalOpen = show;
    this.refreshIcons();
  }

  triggerToast(msg: string): void {
    this.toastMessage = msg;
    this.showToast = true;
    setTimeout(() => {
      this.showToast = false;
    }, 3000);
  }

  // Helper Mock Data Generator
  private generateMockReports(count: number): ReportItem[] {
    const vessels = ['M/T Nordic Mariner', 'M/V Pacific Pioneer', 'Starlight Chemist', 'Port of Houston', 'Port of Antwerp'];
    const formats = ['PDF', 'XLSX'];
    const sampleTitles: Record<string, string[]> = {
      'Sailing Performance': ['Noon Speed & Fuel Analysis', 'Hull Fouling Degradation Audit', 'Weather Routing Efficiency'],
      'Port Performance': ['Turnaround Delay Log', 'Berth Waiting Time Audit', 'Stevedore Discharge Rate'],
      'Cargo Heating': ['Boiler Fuel Consumption Log', 'Cargo Temp Log - Tank 3P/3S', 'Thermal Heat Loss Audit'],
      'Disbursement': ['Final DA Audit Statement', 'PDA vs FDA Settlement Variance', 'Bunker Expense Recon'],
      'Demurrage': ['Laytime Statement of Facts', 'Despatch Calculation Sheet', 'Demurrage Claim Summary']
    };

    const list: ReportItem[] = [];
    for (let i = 1; i <= Math.min(count, 35); i++) {
      const mod = this.moduleList[Math.floor(Math.random() * this.moduleList.length)];
      const titleArr = sampleTitles[mod];
      const title = titleArr[Math.floor(Math.random() * titleArr.length)];
      const vessel = vessels[Math.floor(Math.random() * vessels.length)];
      const fmt = formats[Math.floor(Math.random() * formats.length)];
      
      const d = new Date();
      d.setDate(d.getDate() - Math.floor(Math.random() * 25));

      list.push({
        id: `REP-2026-${1000 + i}`,
        module: mod,
        title,
        vessel,
        timestamp: d.toISOString(),
        format: fmt
      });
    }
    return list.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }
}


export type MaritimeModule =
  | 'Sailing Performance'
  | 'Port Performance'
  | 'Cargo Heating'
  | 'Disbursement'
  | 'Demurrage';

// export interface User {
//   id: string;
//   name: string;
//   email: string;
//   role: string;
//   avatarUrl?: string;
// }

export interface ModuleStat {
  module: MaritimeModule;
  count: number;
  percentage: number;
  lastGenerated: string;
}

export interface ReportSummary {
  userId: string;
  userName: string;
  totalReports: number;
  mostActiveModule: MaritimeModule;
  avgWeeklyReports: number;
  moduleBreakdown: Record<MaritimeModule, number>;
  stats: ModuleStat[];
}

export interface ReportActivityItem {
  id: string;
  reportName: string;
  module: MaritimeModule;
  generatedAt: string;
  format: 'PDF' | 'Excel' | 'CSV';
  vesselName: string;
  sizeKb: number;
}


declare var lucide: any;
declare var Chart: any;

export interface User {
  id: string;
  name: string;
  role: string;
  avatar: string;
  department: string;
}

export interface ReportItem {
  id: string;
  module: string;
  title: string;
  vessel: string;
  timestamp: string;
  format: string;
}